import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/Logo'

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('overview')
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'data-collection', label: 'Data We Collect' },
    { id: 'how-we-use', label: 'How We Use Data' },
    { id: 'cookies', label: 'Cookies & Tracking' },
    { id: 'your-rights', label: 'Your Rights' },
    { id: 'data-security', label: 'Data Security' },
    { id: 'contact', label: 'Contact Us' }
  ]

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && sections.some(s => s.id === hash)) {
      setActiveSection(hash)
      const element = document.getElementById(hash)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }, [])

  const handleTabChange = (value) => {
    setActiveSection(value)
    window.history.replaceState(null, '', `#${value}`)
    const element = document.getElementById(value)
    if (element) {
      const headerOffset = 100
      const rect = element.getBoundingClientRect()
      const y = window.pageYOffset + rect.top - headerOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
        {/* Header */}
        <header className="sticky top-0 z-50 py-4 bg-white border-b border-neutral-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="font-semibold text-xl tracking-tight flex items-center gap-2 text-black">
                <LogoMark 
                  width={32} 
                  height={18} 
                  color="#000000"
                  className="flex-shrink-0"
                />
                <span className="hidden sm:inline">Dom Murphy</span>
              </Link>
              <Button 
                as={Link} 
                to="/" 
                variant="secondary" 
                size="sm"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            {/* Title */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading mb-4">
                Privacy Policy
              </h1>
              <p className="text-neutral-600 text-lg">
                Last updated on 1st July 2025
              </p>
            </div>

            {/* Sticky Navigation Tabs */}
            <div className="sticky top-20 z-40 mb-12 bg-white py-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-b border-neutral-200">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleTabChange(section.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      activeSection === section.id
                        ? 'bg-neutral-900 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-neutral max-w-none">
              {/* Overview */}
              <section id="overview" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">1. Overview</h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>
                    This Privacy Policy explains how Dom Murphy ("we", "our", or "us") collects, uses, and protects your personal information when you visit our website at <a href="https://www.dommurphy.co.uk/" className="text-orange-600 hover:text-orange-700 underline">https://www.dommurphy.co.uk/</a>.
                  </p>
                  <p>
                    We are committed to protecting your privacy and complying with applicable data protection legislation in the United Kingdom, including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                  </p>
                  <p>
                    By using our website, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our website.
                  </p>
                </div>
              </section>

              {/* Data We Collect */}
              <section id="data-collection" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">2. Data We Collect</h2>
                <div className="space-y-6 text-neutral-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-3">2.1 Information You Provide</h3>
                    <p className="mb-2">We may collect personal information that you voluntarily provide to us, including:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Name and contact information (email address, phone number)</li>
                      <li>Information provided when booking a call or consultation</li>
                      <li>Any communications you send to us</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-3">2.2 Automatically Collected Information</h3>
                    <p className="mb-2">When you visit our website, we may automatically collect certain information, including:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>IP address and device information</li>
                      <li>Browser type and version</li>
                      <li>Pages visited and time spent on pages</li>
                      <li>Referring website addresses</li>
                      <li>Date and time of your visit</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-3">2.3 Cookies and Tracking Technologies</h3>
                    <p>
                      We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. For more information about cookies, please see section 4 below.
                    </p>
                  </div>
                </div>
              </section>

              {/* How We Use Data */}
              <section id="how-we-use" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">3. How We Use Your Data</h2>
                <div className="space-y-6 text-neutral-700 leading-relaxed">
                  <p>We use the information we collect for the following purposes:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>To provide our services:</strong> To respond to your enquiries and schedule consultations</li>
                    <li><strong>To improve our website:</strong> To analyse how visitors use our website and improve user experience</li>
                    <li><strong>To communicate with you:</strong> To send you updates, respond to your questions, and provide customer support</li>
                    <li><strong>To comply with legal obligations:</strong> To meet our legal and regulatory requirements</li>
                    <li><strong>For marketing purposes:</strong> With your consent, to send you promotional materials about our services (you can opt out at any time)</li>
                  </ul>
                  <p>
                    Our legal basis for processing your personal data includes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Your consent (where you have provided it)</li>
                    <li>Performance of a contract or to take steps before entering into a contract</li>
                    <li>Our legitimate interests (such as improving our services and website functionality)</li>
                    <li>Compliance with legal obligations</li>
                  </ul>
                </div>
              </section>

              {/* Cookies */}
              <section id="cookies" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">4. Cookies and Tracking Technologies</h2>
                <div className="space-y-6 text-neutral-700 leading-relaxed">
                  <p>
                    We use various cookies and tracking technologies on our website:
                  </p>
                  
                  <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">4.1 Google Analytics</h3>
                    <p className="mb-3">
                      We use Google Analytics to track website performance and understand how visitors interact with our website. Google Analytics collects information such as:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-3">
                      <li>How you found our website</li>
                      <li>Pages you visit and time spent on each page</li>
                      <li>Device and browser information</li>
                      <li>Geographic location (country/city level)</li>
                    </ul>
                    <p>
                      This data is collected anonymously and used to improve our website. Google's privacy policy can be found at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">https://policies.google.com/privacy</a>.
                    </p>
                  </div>

                  <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">4.2 Meta Pixel (Optional)</h3>
                    <p className="mb-3">
                      We may use the Meta Pixel (formerly Facebook Pixel) to measure the effectiveness of our advertising and to provide you with relevant advertisements on Facebook and Instagram. The Meta Pixel tracks:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-3">
                      <li>Website visits and page views</li>
                      <li>Actions taken on our website</li>
                      <li>Conversion events (such as booking a call)</li>
                    </ul>
                    <p>
                      You can opt out of Facebook's use of cookies by visiting <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Facebook's Ad Settings</a>. Meta's privacy policy can be found at <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">https://www.facebook.com/privacy/explanation</a>.
                    </p>
                  </div>

                  <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">4.3 TikTok Pixel (Optional)</h3>
                    <p className="mb-3">
                      We may use the TikTok Pixel to measure advertising effectiveness and show you relevant ads on TikTok. The TikTok Pixel collects:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-3">
                      <li>Website activity and conversions</li>
                      <li>Product interactions</li>
                      <li>Device information</li>
                    </ul>
                    <p>
                      You can manage your TikTok advertising preferences in your TikTok account settings. TikTok's privacy policy can be found at <a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">https://www.tiktok.com/legal/privacy-policy</a>.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-3">4.4 Managing Cookies</h3>
                    <p>
                      You can control cookies through your browser settings. Most browsers allow you to refuse cookies or alert you when cookies are being sent. However, if you disable cookies, some parts of our website may not function properly.
                    </p>
                  </div>
                </div>
              </section>

              {/* Your Rights */}
              <section id="your-rights" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">5. Your Rights Under UK GDPR</h2>
                <div className="space-y-6 text-neutral-700 leading-relaxed">
                  <p>
                    Under UK GDPR, you have the following rights regarding your personal data:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right of Access</h3>
                      <p>You have the right to request copies of your personal data that we hold.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right to Rectification</h3>
                      <p>You have the right to request that we correct any inaccurate or incomplete personal data.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right to Erasure</h3>
                      <p>You have the right to request that we delete your personal data in certain circumstances.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right to Restrict Processing</h3>
                      <p>You have the right to request that we restrict the processing of your personal data in certain circumstances.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right to Data Portability</h3>
                      <p>You have the right to request the transfer of your personal data to another organisation.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Right to Object</h3>
                      <p>You have the right to object to our processing of your personal data for certain purposes, such as direct marketing.</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-neutral-200">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-2">Rights Related to Automated Decision-Making</h3>
                      <p>You have the right not to be subject to decisions based solely on automated processing.</p>
                    </div>
                  </div>

                  <p>
                    To exercise any of these rights, please contact us using the details provided in section 7 below. We will respond to your request within one month.
                  </p>
                </div>
              </section>

              {/* Data Security */}
              <section id="data-security" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">6. Data Security</h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>
                    We take the security of your personal data seriously and implement appropriate technical and organisational measures to protect it against unauthorised access, alteration, disclosure, or destruction.
                  </p>
                  <p>
                    However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.
                  </p>
                  <p>
                    We retain your personal data only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section id="contact" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl font-bold tracking-tight font-heading mb-6">7. Contact Us</h2>
                <div className="bg-neutral-50 rounded-xl p-8 border border-neutral-200">
                  <p className="text-neutral-700 leading-relaxed mb-4">
                    If you have any questions about this Privacy Policy, wish to exercise your rights, or have concerns about how we handle your personal data, please contact us:
                  </p>
                  <div className="space-y-2 text-neutral-700">
                    <p><strong>Dom Murphy</strong></p>
                    <p>Website: <a href="https://www.dommurphy.co.uk/" className="text-orange-600 hover:text-orange-700 underline">https://www.dommurphy.co.uk/</a></p>
                  </div>
                  <p className="text-neutral-700 leading-relaxed mt-6">
                    You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe we have not handled your personal data in accordance with UK GDPR. You can contact the ICO at:
                  </p>
                  <div className="mt-4 space-y-2 text-neutral-700">
                    <p><strong>Information Commissioner's Office</strong></p>
                    <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">https://ico.org.uk</a></p>
                    <p>Phone: 0303 123 1113</p>
                  </div>
                </div>
              </section>

              {/* Changes to Policy */}
              <section className="mb-16 scroll-mt-24 border-t border-neutral-200 pt-8">
                <h2 className="text-2xl font-bold tracking-tight font-heading mb-4">8. Changes to This Privacy Policy</h2>
                <p className="text-neutral-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-10 border-t border-neutral-200" role="contentinfo">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <p className="text-sm text-neutral-500">© {new Date().getFullYear()} Dom. All rights reserved.</p>
            <div className="text-sm text-neutral-500 flex items-center gap-4">
              <Link to="/" className="hover:text-black/70">Back to Home</Link>
            </div>
          </div>
        </footer>
      </div>
  )
}

